import { useEffect, useRef } from "react";
import {
  getDocumentScrollState,
  resolveRestoredScrollTop,
  saveDocumentScrollState,
} from "@/lib/documentScrollMemory";
import { findScrollContainer } from "../utils/scrollUtils";

/**
 * The element that actually scrolls the document.
 *
 * Walks up from the rendered root rather than trusting one selector, because the
 * two viewers scroll different things: the legislation view scrolls the page
 * shell (`[data-content-scroll]`), the workspace document view its own
 * `overflow-auto` wrapper. Falls back to {@link findScrollContainer} so this can
 * never resolve to nothing.
 */
const resolveScrollContainer = (
  docRoot: HTMLElement | null
): HTMLElement | null => {
  for (let el: HTMLElement | null = docRoot; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    const scrolls =
      overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
    if (scrolls && el.scrollHeight > el.clientHeight) return el;
  }
  return findScrollContainer(docRoot);
};

/**
 * Restores the reader's position in a document across page loads, and records it
 * again before the page goes away (LAR-361).
 *
 * Saving is tied to `visibilitychange` and `pagehide` rather than to every scroll
 * event: those are exactly the moments before a tab is discarded, reloaded, or
 * sent through a full-page re-login, and it keeps a long scroll from writing to
 * `sessionStorage` on every frame.
 *
 * @param documentKey Stable identity of the document on screen. Undefined disables
 *   the memory entirely — used for renders that must not participate, such as the
 *   print-only copy and sidebar link previews.
 * @param hasDeepLinkTarget True when the URL asks for a specific section. An
 *   explicit destination always wins over a remembered position.
 */
export const useDocumentScrollMemory = (
  docRootRef: React.RefObject<HTMLDivElement | null>,
  isContentRendered: boolean,
  documentKey: string | undefined,
  hasDeepLinkTarget: boolean
): void => {
  /** The key we have already restored, so re-renders don't yank the reader back. */
  const restoredKeyRef = useRef<string | null>(null);
  /**
   * The reader's latest observed offset. Needed for the unmount save alone: that
   * runs in the passive phase, after React has detached the document and nulled
   * the ref, where a live read resolves to the surviving page shell sitting at 0
   * and wipes the memory instead of keeping it. `null` means never observed.
   */
  const latestScrollTopRef = useRef<number | null>(null);

  // Restoring and tracking share one effect so they also share one resolved
  // container. Resolving twice was a defect: the answer depends on live layout,
  // so a call before the document lays out finds no overflowing ancestor and
  // falls back to the page shell, while a call two frames later finds the real
  // scroller. The tracker then watched an element the restore never scrolled.
  useEffect(() => {
    if (!documentKey || !isContentRendered) return;

    let cancelled = false;
    let innerFrameId = 0;
    let container: HTMLElement | null = null;

    const record = () => {
      if (container) latestScrollTopRef.current = container.scrollTop;
    };

    const persist = () => {
      // Live read while the document is still on screen — that needs no scroll
      // event to have fired. The recorded offset is for the unmount save, which
      // runs after React has detached the document. With neither, we have never
      // observed this reader's position: leave what is stored alone rather than
      // saving a 0 that would delete it.
      const offset = container?.isConnected
        ? container.scrollTop
        : latestScrollTopRef.current;
      if (offset === null || offset === undefined) return;

      saveDocumentScrollState(documentKey, offset);
    };

    const persistIfHidden = () => {
      if (document.visibilityState === "hidden") persist();
    };

    // Registered up front so a tab hidden during the frames below still saves —
    // it just has nothing new to record yet, and keeps the stored position.
    document.addEventListener("visibilitychange", persistIfHidden);
    window.addEventListener("pagehide", persist);

    const start = () => {
      if (cancelled) return;

      container = resolveScrollContainer(docRootRef.current);
      if (!container) return;

      if (!hasDeepLinkTarget) {
        if (restoredKeyRef.current !== documentKey) {
          const saved = getDocumentScrollState(documentKey);
          if (saved) {
            container.scrollTop = resolveRestoredScrollTop(saved, container);
          }
          restoredKeyRef.current = documentKey;
        }
        // Seeded so a document the reader never scrolls further still saves
        // honestly. Deliberately not seeded for a deep-linked render: the URL is
        // driving the position, and the section it scrolls to may not have been
        // reached yet — recording that 0 would delete an earlier visit's place.
        latestScrollTopRef.current = container.scrollTop;
      }

      container.addEventListener("scroll", record, { passive: true });
    };

    // Two frames, matching the chat restore: the first commits the parsed
    // document, the second lets layout settle so scrollHeight is real.
    const outerFrameId = requestAnimationFrame(() => {
      if (cancelled) return;
      innerFrameId = requestAnimationFrame(start);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(innerFrameId);
      container?.removeEventListener("scroll", record);
      document.removeEventListener("visibilitychange", persistIfHidden);
      window.removeEventListener("pagehide", persist);
      // Leaving the document (a reference, another law, the back button) is also
      // worth remembering, so returning within the session lands in place.
      persist();
      latestScrollTopRef.current = null;
    };
  }, [docRootRef, documentKey, hasDeepLinkTarget, isContentRendered]);
};
